import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";

import { EntityPeekHint } from "@/components/hub/EntityPeekHint";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { manufacturingLocationShortLabel } from "@/lib/manufacturingLocationChip";
import { cn } from "@/lib/utils";
import { megaventoryUrlFor } from "@/pages/manufacturing/workOrdersPageModel";

import {
  clampQcWorkOrderStatusChipLabel,
  type ClampQcReportingRollupRow,
} from "./clampQcBuildOrdersApi";
const DETAIL_LINK_CLASS =
  "font-medium text-sky-700 hover:text-sky-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1";

function ClampQcReportingWorkOrderPeekHint({ row }: { row: ClampQcReportingRollupRow }) {
  return (
    <EntityPeekHint
      kind="work_order_inline"
      context={{
        sku: row.sku,
        location_label: manufacturingLocationShortLabel(row.locationId, row.locationCode),
        mv_status: clampQcWorkOrderStatusChipLabel(row.status, row.hubStatus),
        planned_quantity: row.plannedQuantity,
        quantity_complete: row.quantityComplete,
        fail_count: row.failCount,
        avg_line_harvest_delta: row.avgLineHarvestDelta,
      }}
    />
  );
}

export function ClampQcReportingWorkOrderCell({ row }: { row: ClampQcReportingRollupRow }) {
  const detailHref = `/clamp-qc/reporting/jobs/${encodeURIComponent(row.slug)}`;
  const megaventoryHref = megaventoryUrlFor({
    mv_id: row.mvId,
    mv_number: row.mvNumber,
  });

  return (
    <div className="flex items-center gap-1">
      <HoverHintTrigger
        align="start"
        closeOnTriggerActivate
        trigger={
          <Link to={detailHref} className={cn(DETAIL_LINK_CLASS, "font-mono text-xs")}>
            {row.workOrder}
          </Link>
        }
      >
        <ClampQcReportingWorkOrderPeekHint row={row} />
      </HoverHintTrigger>
      {megaventoryHref != null ? (
        <a
          href={megaventoryHref}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex shrink-0 rounded p-0.5 text-sky-700 hover:text-sky-900"
          aria-label={`Open ${row.workOrder} in Megaventory`}
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      ) : null}
    </div>
  );
}
